/* eslint-disable @typescript-eslint/no-explicit-any */
import type { isMatchWithCustomizer } from 'lodash';
import {
  escapeRegExp,
  isArray,
  isEqual,
  isMatch,
  isMatchWith,
  isString,
  orderBy,
  sortBy as _sortBy,
  union,
  uniqBy,
  uniqueId
} from 'lodash';
import { action, computed, IObservableArray, observable } from 'mobx';
import safelyParseJSON from '@kentik/ui-shared/util/safelyParseJson';

import api from 'core/util/api';
import collatePromises from 'core/util/collatePromises';
import { processLargeArrayAsync } from 'core/util';
import GenericModel, { Model } from 'core/model/Model';
import { InferModelConstructorArgs, InferModelType } from 'core/model/model.types';
import { WithOptional } from '@kentik/ui-shared/types/util';

/**
 * The set of keys that make up the type underlying a targeted model
 */
export type ModelKey<MOD extends { attributes: object }> = string & keyof InferModelType<MOD>;

/**
 * The acceptable sort states for a given Collection
 */
export type CollectionSortState<MOD extends Model<any, any>> = {
  field?: ModelKey<MOD>;
  direction?: 'asc' | 'desc';
  value?: string;
};

/**
 * The acceptable grouping states for a given Collection.
 */
export type CollectionGroupBy<MOD extends Model<any, any>> =
  | ModelKey<MOD>
  | null
  | { groupByFn: (model: MOD) => string };

type Filter<T extends Model<any, any>> = { fn: (value: T, index: number, array: T[]) => unknown };

type PresetFilter<T extends Model<any, any>> = { default?: string } & Filter<T>;

type DiscreteFilter<T extends Model<any, any>> = { type: string } & CollectionSortState<T> & Filter<T>;

/**
 * The constructor of the Model owned by this Collection.
 */
export type ModelConstructor<M extends Model<any, any>> = new (attributes?: any) => M;

interface FetchData<MOD extends Model<any, any>> {
  force?: boolean;
  sortBy?: string | ((model: MOD) => boolean);
  offset?: number;
  pagination?: { offset?: number };
  window?: { startDate: number; endDate: number };
  [key: string]: unknown;
}

export interface FetchOptions<MOD extends Model<any, any>> {
  data?: FetchData<MOD>;
  fetchUrl?: string;
  fetchMethod?: 'get' | 'post';
  preserveSelection?: boolean;
  query?: object;
  pagedFetch?: boolean;
  force?: boolean;
  hard?: boolean;
  idPath?: ModelKey<MOD>;
  // Mostly useful if you want to check headers (and by default results in setting totalCount if applicable)
  rawResponse?: boolean;
  // Allows you to reuse previous responses
  withCache?: boolean;
}

export interface CollectionOptions<MOD extends Model<any, any>> {
  threeWaySort?: boolean;
  hasFetched?: boolean;
  models?: MOD[];
  store?: object;
  // eslint-disable-next-line no-use-before-define
  parent?: any;
  groupBy?: CollectionGroupBy<MOD>;
  sortState?: CollectionSortState<MOD>;
  activePresetFilter?: PresetFilter<MOD>;
  discreteFilters?: DiscreteFilter<MOD>[];
  getSortValue?: (field: (string & keyof InferModelType<MOD>) | undefined, model: MOD) => string;
}

export type RequestStatus = 'fetching' | 'fetchingMore' | 'awaitingRequest' | 'updating';

/**
 * Like regular Model JSON except with additional fields that come from the API. Note that all
 * API fields are marked as optional - this means that you can also pass in regular objects and
 * everything will still function!
 */
export type ApiResponse<MOD extends Model<any, any>> = InferModelConstructorArgs<MOD>[] & {
  pagination?: { has_more_results?: boolean };
  headers?: Record<string, string>;
  body?: string;
};

/**
 * Every form of acceptable Model input. This includes:
 * - the Model itself,
 * - ModelConstructorArgs - just the JSON required to construct the object.
 * - Model Type (i.e. Model.attributes)
 *   - technically a superset of ModelConstructorArgs but TS doesn't automatically understand that!
 *
 * Note that sometimes TS errors can be confusing here when it tests against the Model.
 * Consider asserting InferModelConstructorArgs<MOD>.
 */
export type AcceptedModelInputs<MOD extends Model<any, any>> =
  | WithOptional<InferModelType<MOD>, 'id'>
  | InferModelConstructorArgs<MOD>
  | MOD;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export abstract class Collection<MOD extends Model<any, any>> {
  @observable
  public requestStatus: RequestStatus | null = null;

  public models: IObservableArray<MOD> = observable.array([], { deep: false });

  public modelById: Record<string, MOD> = {};

  /**
   * list of fields that, when used to sort, will trigger a server-side data load
   *
   * Ideally this would be protected and readonly but we do set it externally in some cases
   */
  public serverSortableFields: string[] = [];

  // list of fields that, when used to sort, will need ui-driven logic to create a sorting filter for API
  protected readonly uiSortLogicFields: (string | CollectionGroupBy<MOD>)[] = [];

  @observable.shallow
  public error?: { label: string; body?: unknown };

  public id = uniqueId('c_');

  @observable
  public filterState: undefined | ((model: MOD) => boolean);

  @observable
  public activePresetFilter: PresetFilter<MOD> | undefined | null;

  @observable
  public discreteFilters: DiscreteFilter<MOD>[] = [];

  @observable
  public sortState: CollectionSortState<MOD> = {};

  @observable
  public groupBy: CollectionGroupBy<MOD> = null;

  @observable
  public selected: MOD[] | MOD | null = null;

  @computed
  public get selectedArray(): MOD[] {
    if (!this.selected) {
      return [];
    }
    return Array.isArray(this.selected) ? this.selected : [this.selected];
  }

  @observable
  public hasFetched = false;

  @observable.ref
  public original: MOD[] | null = null;

  @observable
  public lastUpdated?: number;

  @observable
  public lastFetched?: number | null;

  @observable
  public useAsyncAdd = false;

  public fetchPromise: Promise<InferModelConstructorArgs<MOD>[] | null> | null = null;

  @observable
  public totalCount?: number;

  @observable
  public hasMoreResults?: boolean;

  protected readonly threeWaySort?: boolean;

  protected readonly store?: object;

  // eslint-disable-next-line no-use-before-define,@typescript-eslint/no-explicit-any
  public readonly parent?: any;

  public getSortValue?: (field: (string & keyof InferModelType<MOD>) | undefined, model: MOD) => string;

  @observable
  private fetchCount = 0;

  private lastData?: FetchData<MOD> | null;

  @observable
  private cachedResults: Map<string, ApiResponse<MOD>> = new Map();

  hashModelsById = (): void => {
    const modelHash: Record<string, MOD> = {};
    this.get().forEach((model: MOD) => {
      if (model && model.id) {
        modelHash[model.id] = model;
      }
    });
    this.modelById = modelHash;
  };

  public constructor(data: AcceptedModelInputs<MOD>[] = [], options: CollectionOptions<MOD> = {}) {
    const {
      activePresetFilter = this.defaultPresetFilter,
      sortState = this.defaultSortState,
      discreteFilters = this.defaultDiscreteFilters,
      groupBy: currGroupBy = this.defaultGroupBy,
      getSortValue,
      hasFetched = false,
      models,
      threeWaySort = false,
      store = null
    } = options;

    if (store) {
      this.store = store;
    }

    this.parent = options.parent || undefined;

    // allows for custom getSortValue in generic Collection usage
    if (typeof getSortValue === 'function') {
      this.getSortValue = getSortValue;
    }

    // manual bind for converted arrow functions. Allows subclasses to override but keeps arrow function "this" behavior.
    this.processData = this.processData.bind(this);
    this.reset = this.reset.bind(this);

    this.activePresetFilter = activePresetFilter;

    this.threeWaySort = threeWaySort;
    this.sortState = sortState;
    this.discreteFilters = discreteFilters;
    this.groupBy = currGroupBy;

    if (models) {
      this.models.replace(models);
      this.hashModelsById();
    } else {
      this.set(data);
    }

    // Most common use case for this would be when a collection is initialized with data upfront.
    // When a collection is initialized with data and a PresetFilterButton group is applied to it,
    // you may encounter a situation where the filter buttons all become disabled when filtering by a property
    // that yields 0 results. Setting this option will prevent that.
    this.hasFetched = hasFetched;
  }

  public get urlPaths(): undefined | { destroy?: string; fetch?: string; update?: string } {
    return undefined;
  }

  protected get fetchMethod(): 'get' | 'post' {
    return 'get';
  }

  protected get queuedFetchKey(): string | null {
    return null;
  }

  protected get defaultSortState(): CollectionSortState<MOD> {
    return {};
  }

  protected get defaultDiscreteFilters(): DiscreteFilter<MOD>[] {
    return [];
  }

  protected get baseSort(): CollectionSortState<MOD> | null {
    return null;
  }

  protected get secondarySort(): CollectionSortState<MOD> | null {
    return null;
  }

  protected get defaultGroupBy(): CollectionGroupBy<MOD> {
    return null;
  }

  protected get defaultPresetFilter(): PresetFilter<MOD> | undefined {
    return (this.presetFilters && this.presetFilters.find((filter) => filter.default)) || undefined;
  }

  public get presetFilters(): false | PresetFilter<MOD>[] {
    return false;
  }

  /**
   * Appears entirely unused? Should probably be deleted but I'm leaving it in for now.
   * @private
   * @deprecated should be removed if this continues to go unused
   */
  private get discreteFiltersTypes(): string[] {
    return this.discreteFilters.map((filter) => filter.type);
  }

  public get minFetchInterval(): number {
    return 300000;
  }

  /**
   * Returns the URL where the model's resource would be located on the server.
   */
  public get url(): string | undefined {
    return undefined;
  }

  /**
   * Specifies the model class for that collection
   */
  public abstract get model(): ModelConstructor<MOD>;

  /**
   * Returns this Collection's Models as raw JSON.
   */
  public toJS(this: this): InferModelType<MOD>[] {
    return this.models.map((model) => model.toJS());
  }

  /**
   * Describes the behavior of what a server side sort means, can be overridden at the collection level
   */
  public serverSortFn(this: this): Promise<InferModelConstructorArgs<MOD>[] | null> | Promise<void> {
    return this.loadMoreItems({ startIndex: 0, force: true, reset: true });
  }

  /**
   * Creates a new model instance with the given attributes
   */
  public build = (
    attributes: AcceptedModelInputs<MOD>,
    options: { select?: boolean; deserialize?: boolean } = {}
  ): MOD => {
    const { select = false, deserialize = true } = options;
    const ModelClass = this.model;
    const model = new ModelClass();

    model.store = this.store;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    model.collection = this as Collection<any>;
    model.set(deserialize ? model.deserialize(attributes) : attributes);

    if (select) {
      this.selected = model;
    }

    return model;
  };

  /**
   * Questions whether the request exists and matches a certain label
   * If no label are passed it will check if any requests are active.
   */
  public isRequestActive(this: this, label?: string): boolean {
    if (this.requestStatus === 'awaitingRequest') {
      return true;
    }
    if (!label) {
      return this.requestStatus !== null;
    }

    return !!this.requestStatus && this.requestStatus === label;
  }

  /**
   * Whether the collection is empty
   */
  public isEmpty(this: this): boolean {
    return this.models.length === 0;
  }

  /**
   * Get a resource at a given position
   */
  public at(this: this, index: number): MOD | false {
    if (index < 0 || index >= this.size || !this.models[index]) {
      console.warn(`No model found at index ${index}`);
      return false;
    }

    return this.models[index];
  }

  /**
   * Like `this.at()` except safer since it returns null when no result
   */
  public att(this: this, index: number): MOD | null {
    return this.at(index) || null;
  }

  public get(this: this): IObservableArray<MOD>;

  public get(this: this, id: InferModelType<MOD>['id']): MOD | undefined;

  /**
   * Get a resource with the given id or uuid or if no id is provided, will return all the models
   */
  public get(this: this, id?: InferModelType<MOD>['id']): MOD | MOD[] | undefined {
    if (id !== undefined) {
      return this.modelById[id];
    }
    const models = this.original || this.models;
    return models.slice();
  }

  public [Symbol.iterator](this: this): Iterator<MOD> {
    return this.models[Symbol.iterator]();
  }

  public each(this: this, iterator: (model: MOD) => void): void {
    this.models.forEach(iterator);
  }

  public map<R>(this: this, iterator: (model: MOD) => R): R[] {
    return this.models.map(iterator);
  }

  public some(this: this, iterator: (model: MOD) => boolean): boolean {
    return this.models.some(iterator);
  }

  public every(this: this, iterator: (model: MOD) => boolean): boolean {
    return this.models.every(iterator);
  }

  public reduce<R>(this: this, iterator: (acc: R, model: MOD) => R, initialValue: R): R {
    return this.models.reduce(iterator, initialValue);
  }

  public slice(this: this, start?: number, end?: number): MOD[] {
    return this.models.slice(start, end);
  }

  /**
   * Standard JS array.find()
   */
  public find(this: this, query: (model: MOD, index: number) => boolean): MOD | undefined;

  /**
   * Lodash predicate search
   */
  public find(
    this: this,
    query: (Record<string, unknown> & ({ bind?: never } | { call?: never })) | object[],
    customizer?: isMatchWithCustomizer
  ): MOD | undefined;

  /**
   * Finds an element with the given matcher
   */
  public find(
    this: this,
    query:
      | ((model: MOD, index: number) => boolean)
      | (Record<string, unknown> & ({ bind?: never } | { call?: never }))
      | object[],
    customizer?: isMatchWithCustomizer
  ): MOD | undefined {
    // standard JS array.find()
    if (typeof query === 'function') {
      return this.models.find(query);
    }
    // lodash predicate find
    return this.models.find((model) =>
      customizer ? isMatchWith(model.attributes, query, customizer) : isMatch(model.attributes, query)
    );
  }

  @action
  public group(this: this, value: string & keyof MOD): void {
    const loadMore =
      this.groupBy !== value && (value ? this.serverSortableFields.includes(value) : this.sortFieldIsServerSortable);

    this.groupBy = value;

    // if grouping by a new column that's server sortable, we also need to re-fetch
    if (loadMore) {
      this.loadMoreItems({ startIndex: 0, force: true, reset: true });
    }
  }

  public get isTreeGroupBy(): boolean {
    return false;
  }

  public get groupedData(): Record<string, MOD[]> {
    if (!this.groupBy) {
      return {};
    }

    const groups: Record<string, MOD[]> = {};

    this.models.forEach((model) => {
      // add ability to set this.groupBy to object. Expecting type(string) and groupByFn(function) properties.
      const group: string | number =
        typeof this.groupBy === 'object'
          ? this.groupBy?.groupByFn(model)
          : ((model as InferModelType<MOD>)[this.groupBy] ?? model.get(this.groupBy));

      if (Array.isArray(group)) {
        if (group.length > 0) {
          // add model to multiple groups (for labels, etc.)
          group.forEach((g) => {
            groups[g] ??= [];
            groups[g].push(model);
          });
        } else {
          groups.None ??= [];
          groups.None.push(model);
        }
      } else {
        groups[group] ??= [];
        groups[group].push(model);
      }
    });

    return groups;
  }

  public get sortedGroupKeys(): string[] {
    if (!this.groupBy) {
      return [];
    }

    const groupedModels = this.groupedData;

    // This assumed ALL the models are sorted, then this sorts the group indexes based on their current order in the whole collection
    return _sortBy(Object.keys(groupedModels), (groupKey) => this.models.indexOf(groupedModels[groupKey][0]));
  }

  @action
  public move(this: this, oldIndex: number, newIndex: number): void {
    this.models.splice(newIndex, 0, this.models.splice(oldIndex, 1)[0]);
  }

  @action
  public sort(this: this, field?: string & keyof InferModelType<MOD>, direction?: 'asc' | 'desc'): void {
    let sortField = this.sortState.field;
    let sortDir = this.sortState.direction;

    // just clear the sort if the user clicked the same field while already sorted descending or sorted by the default direction
    if (
      this.threeWaySort &&
      sortField === field &&
      sortDir &&
      ((this.defaultSortState.direction && sortDir !== this.defaultSortState.direction) ||
        (!this.defaultSortState.direction && sortDir === 'desc'))
    ) {
      this.clearSort();

      return;
    }

    // If a field was explicitly passed, use it
    if (field !== undefined) {
      sortField = field;
      sortDir = direction || this.defaultSortState.direction || 'asc';

      // If the field passed was the same as current, toggle the direction (or turn off)
      if (field === this.sortState.field && !direction) {
        sortDir = this.sortState.direction === 'asc' ? 'desc' : 'asc';
      }
    }

    if (sortField) {
      if (this.sortState.field !== sortField || this.sortState.direction !== sortDir) {
        this.sortState = {
          field: sortField,
          direction: sortDir
        };
      }

      // no need to sort locally if we already sorted on the server
      if (field === undefined && this.sortFieldIsServerSortable) {
        return;
      }

      this.original = this.get();

      const iteratees = [];
      const orders: (boolean | 'asc' | 'desc')[] = [];

      let baseSortFn;
      let sortFn;
      const { getSortValue } = this;
      if (getSortValue) {
        baseSortFn = (model: MOD) => getSortValue(this.baseSort?.field, model);
        sortFn = (model: MOD) => getSortValue(sortField, model);
      } else {
        baseSortFn = (model: MOD) => model.getSortValue(this.baseSort?.field);
        sortFn = (model: MOD) => model.getSortValue(sortField);
      }

      if (this.baseSort) {
        iteratees.push(baseSortFn);
        orders.push(this.baseSort.direction || 'asc');
      }

      iteratees.push(sortFn);
      if (sortDir) {
        orders.push(sortDir);
      }

      if (this.secondarySort) {
        iteratees.push((model: MOD) => model.getSortValue(this.secondarySort?.field));
        orders.push(this.secondarySort.direction || 'asc');
      }

      this.models.replace(orderBy(this.models, iteratees, orders));
    }
  }

  @action
  public serverSort(this: this, field?: string & keyof InferModelType<MOD>, direction?: 'asc' | 'desc'): void {
    let sortField = this.sortState.field;
    let sortDir = this.sortState.direction;

    // just clear the sort if the user clicked the same field while already sorted descending or sorted by the default direction
    if (
      this.threeWaySort &&
      sortField === field &&
      sortDir &&
      ((this.defaultSortState.direction && sortDir !== this.defaultSortState.direction) ||
        (!this.defaultSortState.direction && sortDir === 'desc'))
    ) {
      this.clearSort(true);

      return;
    }

    // If a field was explicitly passed, use it
    if (field !== undefined) {
      sortField = field;
      sortDir = direction || this.defaultSortState.direction || 'asc';

      // If the field passed was the same as current, toggle the direction (or turn off)
      if (field === this.sortState.field && !direction) {
        sortDir = this.sortState.direction === 'asc' ? 'desc' : 'asc';
      }
    }

    if (sortField) {
      if (this.sortState.field !== sortField || this.sortState.direction !== sortDir) {
        this.sortState = {
          field: sortField,
          direction: sortDir
        };
      }
    }

    if (this.serverSortFn) {
      this.serverSortFn();
    }
  }

  /**
   * Sets the sort state without forcing a sort. Useful to do prior to fetching.
   */
  @action
  public setSortState(this: this, field?: string & keyof InferModelType<MOD>, direction: 'asc' | 'desc' = 'asc'): void {
    this.sortState = { field, direction };
  }

  @action
  public clearSort(this: this, serverSort?: boolean): void {
    this.sortState = {};

    if (this.threeWaySort && !serverSort && this.original) {
      this.set(this.original);
    } else if (serverSort) {
      this.serverSort();
    } else {
      this.sort();
    }
  }

  /**
   * Filter models, finishing with a sort. DO NOT CALL IN A LOOP.
   */
  @action
  public filter(
    this: this,
    query?: (model: MOD) => boolean,
    options: {
      immutable?: boolean;
      idPath?: string & keyof InferModelType<MOD>;
      customFilter?: (model: MOD) => boolean;
      guardForDuplicates?: boolean;
    } = {}
  ): MOD[] {
    const { immutable = false, idPath = 'id', customFilter, guardForDuplicates } = options;

    if (immutable) {
      if (!query) {
        return this.get();
      }
      return this.get().filter(query);
    }

    if (query !== undefined) {
      this.filterState = query;
    }
    this.original = this.get();

    if (customFilter) {
      this.models.replace(this.original.filter(customFilter));
    } else if (this.filterState) {
      if (isString(this.filterState)) {
        const regex = new RegExp(escapeRegExp(this.filterState), 'i');

        this.models.replace(
          this.original.filter((model) => this.getFilterValues(model).some((value) => regex.test(value as string)))
        );
      } else {
        this.models.replace(this.original.filter(this.filterState));
      }
    } else {
      // this is needed to get initial data in collections when filter is applied on first renders
      this.models.replace(this.get());
    }

    if (this.activePresetFilter) {
      this.models.replace(this.models.filter(this.activePresetFilter.fn));
    }

    this.discreteFilters.forEach((filter) => {
      this.models.replace(this.models.filter(filter.fn));
    });

    if (guardForDuplicates) {
      const uniqueIds: unknown[] = [];

      const uniqueModels = this.models.filter((model) => {
        const id = model.get(idPath);
        const isDuplicate = uniqueIds.includes(id);

        if (!isDuplicate) {
          uniqueIds.push(id);

          return true;
        }

        return false;
      });

      this.models.replace(uniqueModels);
    }

    this.sort();

    return this.models;
  }

  @action
  public setPresetFilter(filter: { default: string; fn: (model: MOD) => boolean } | undefined): void {
    this.activePresetFilter = filter;
    this.filter();
  }

  @action
  public resetDiscreteFilters(): void {
    this.discreteFilters = this.defaultDiscreteFilters;
  }

  @action
  public setDiscreteFilters(discreteFilters: DiscreteFilter<MOD>[]): void {
    this.discreteFilters = [...discreteFilters];
  }

  @action
  public removeDiscreteFilter(type: string): void {
    this.discreteFilters = this.discreteFilters.filter((filter) => filter.type !== type);
  }

  @action
  public clearFilters(): void {
    this.filterState = undefined;
    this.activePresetFilter = null;
    this.discreteFilters = [];
    this.models.replace(this.get());
    this.sort();
  }

  @computed
  protected get hasFilter(): boolean {
    return !!(this.filterState || this.activePresetFilter || this.discreteFilters.length);
  }

  /**
   * Returns an array of values that filters can match against.
   * This can support matching against model properties and any other observables and computed properties.
   * To support observables and computed properties, define the property name in the collection's "filterFieldWhitelist" set.
   */
  public getFilterValues(this: this, model: MOD): InferModelType<MOD>[keyof InferModelType<MOD>][] {
    const modelKeys = Object.keys(model.attributes) as (string & keyof InferModelType<MOD>)[];
    const whiteListKeys: (string & keyof InferModelType<MOD>)[] | null =
      this.filterFieldWhitelist && Array.from(this.filterFieldWhitelist);
    const keys: (string & keyof InferModelType<MOD>)[] = union(modelKeys, whiteListKeys);

    return keys
      .filter((name) => !this.filterFieldBlacklist || !this.filterFieldBlacklist.has(name))
      .filter((name) => !this.filterFieldWhitelist || this.filterFieldWhitelist.has(name))
      .map((name) => model.get(name) || (model as InferModelType<MOD>)[name])
      .filter((value) => value !== 'undefined');
  }

  protected get filterFieldBlacklist(): Set<string & keyof InferModelType<MOD>> | null {
    return null;
  }

  protected get filterFieldWhitelist(): Set<string & keyof InferModelType<MOD>> | null {
    return null;
  }

  @computed
  public get size(): number {
    return this.models.length;
  }

  @computed
  public get selectedSize(): number {
    return this.selectedArray.length;
  }

  @computed
  public get unfilteredSize(): number {
    return this.get().length;
  }

  @computed
  public get unfiltered(): MOD[] {
    return this.original || this.models;
  }

  @computed
  public get status(): RequestStatus | 'none' {
    return this.requestStatus || 'none';
  }

  @computed
  public get loading(): boolean {
    return (
      this.requestStatus === 'fetching' ||
      this.requestStatus === 'fetchingMore' ||
      this.requestStatus === 'awaitingRequest'
    );
  }

  @computed
  public get discreteFilterValues(): Record<string, string | undefined> {
    return this.discreteFilters.reduce(
      (acc, f) => {
        acc[f.type] = f.value;
        return acc;
      },
      {} as Record<string, string | undefined>
    );
  }

  @computed
  get isAllSelected(): boolean {
    const selectableModels = this.models?.filter((model) => model.isSelectable);
    return !!(
      this.selectedArray &&
      this.selectedArray?.length >= selectableModels?.length &&
      /** ensure that all the selected models are the same as the models that are currently being displayed.
       * This is useful when the models are paginated, as changing the page will cause the selected.length to be the same as the selectableModels length. */
      selectableModels?.every((model) =>
        this.selectedArray?.find((selectedModel) => model.get('id') === selectedModel.get('id'))
      )
    );
  }

  @computed
  public get sortFieldIsServerSortable(): boolean {
    return !!(this.sortState.field && this.serverSortableFields.includes(this.sortState.field));
  }

  @computed
  protected get sortFieldIsUISortLogicDriven(): boolean {
    return !!(this.sortState.field && this.uiSortLogicFields.includes(this.sortState.field));
  }

  @computed
  protected get groupByFieldIsUISortLogicDriven(): boolean {
    return !!(this.groupBy && this.uiSortLogicFields.includes(this.groupBy));
  }

  public generateSelectOptions = ({
    valueKey = 'id',
    labelKey,
    sortBy,
    unfiltered = false
  }: {
    valueKey?: string & keyof InferModelType<MOD>;
    labelKey?: string & keyof InferModelType<MOD>;
    sortBy?: string;
    unfiltered?: boolean;
  }): (InferModelType<MOD> & { value?: string; label?: string })[] => {
    const models = unfiltered ? this.unfiltered : this.models;
    const options: (InferModelType<MOD> & { value: string; label: string })[] = models.map((model) => ({
      value: model.get(valueKey) || (model as InferModelType<MOD>)[valueKey],
      label: labelKey ? model.get(labelKey) : model.get(valueKey),
      ...model.get()
    }));

    if (sortBy) {
      return orderBy(options, sortBy);
    }

    return options;
  };

  @action
  public selectAll(): void {
    this.selected = this.isAllSelected ? null : this.models.filter((model) => model.isSelectable);
  }

  @action
  public unselectAll = (): void => {
    this.selected = null;
  };

  @action
  public select = (model: MOD, options: { multi?: boolean; shiftKey?: boolean } = {}): void => {
    const { multi = false, shiftKey } = options;
    const m = model && this.get(model.id);

    if (!m) {
      console.warn(`Model (id: ${model && model.id}) not found in ${this.constructor.name}`);
    }

    if (multi) {
      if (!this.selected || !Array.isArray(this.selected)) {
        this.selected = [];
      }

      if (model.isSelected) {
        const indexOfModel = this.selected.indexOf(model);
        if (indexOfModel >= 0) {
          this.selected = this.selected.slice(0, indexOfModel).concat(this.selected.slice(indexOfModel + 1));
        }

        if (this.selected.length === 0) {
          this.selected = null;
        }

        // adding to a multi selection
      } else {
        // select all the models in between
        if (shiftKey) {
          const firstIndex = this.selected[0].index;

          if (firstIndex > model.index) {
            this.selected = this.models.slice(model.index, firstIndex + 1).filter((item) => item.isSelectable);
          } else {
            this.selected = this.models.slice(firstIndex, model.index).filter((item) => item.isSelectable);
          }

          // Browsers (on some platforms) will try to select text in between the 2 cursor points. Revert that.
          document.getSelection()?.removeAllRanges();
        }

        this.selected = this.selected.concat(model);
      }
    } else {
      this.selected = model;
    }
  };

  @action
  public clearSelection = (): void => {
    this.selected = null;
  };

  @action
  public setLastUpdated = (): void => {
    this.lastUpdated = Date.now();
  };

  @action
  protected setLastFetched = (when: number): void => {
    this.lastFetched = when || Date.now();
  };

  @action
  public setRequestStatus = (status: RequestStatus | null): void => {
    this.requestStatus = status;
  };

  @action
  public reset(options: { hard?: boolean } = {}): void {
    this.models.clear();
    this.original = null;
    this.hasFetched = false;
    this.lastFetched = null;
    this.hasMoreResults = false;
    this.hashModelsById();
    this.requestStatus = null;
    this.fetchPromise = null;
    this.setLastUpdated();

    if (options.hard) {
      this.resetState();
    }
  }

  @action
  public resetState(): void {
    this.hasMoreResults = false;
    this.filterState = undefined;
    this.activePresetFilter = this.defaultPresetFilter;
    this.discreteFilters = this.defaultDiscreteFilters;
    this.sortState = this.defaultSortState;
    this.groupBy = this.defaultGroupBy;
    this.filter();
  }

  @action
  public resetSortState(): void {
    this.sortState = this.defaultSortState;
    this.sort();
  }

  @action
  public resetFetchCache(): void {
    this.lastFetched = null;
  }

  /**
   * Adds one or more models to the collection. Accepts an array of models/raw objects or a simple model/object.
   * Returns the added models.
   *
   * DO NOT CALL INSIDE A LOOP as this is somewhat expensive. Instead, prefer constructing complete collection and passing that in.
   *
   * @param data technically you can pass in single objects as well but object[] is better for type hinting and functionally identical otherwise
   */
  @action
  public add = (data: AcceptedModelInputs<MOD>[]): MOD[] => {
    if (!isArray(data)) {
      data = [data];
    }

    const models = data.map((row) => {
      if (row instanceof Model) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        row.collection = this as Collection<any>;
        return row;
      }
      return this.build(row);
    });
    this.original = uniqBy(this.get().concat(models), (model) => model.id);
    this.filter();
    this.hashModelsById();
    this.setLastUpdated();

    return models;
  };

  @action
  public remove = (
    ids: InferModelType<MOD>['id'] | InferModelType<MOD>['id'][] | string | string[],
    destroyModels = false
  ): void => {
    if (Array.isArray(ids)) {
      ids.forEach((id) => this.remove(id, destroyModels));
    } else {
      const model = this.get(ids);
      if (model) {
        if (this.original) {
          this.original = this.original.filter((m) => m !== model);
        }

        this.models.remove(model);

        if (destroyModels) {
          model.destroy();
        }
        if (Array.isArray(this.selected)) {
          const selected = this.selected.filter((selectedModel) => selectedModel.id !== model.id);
          this.selected = selected.length > 0 ? selected : null;
        } else if (this.selected?.id === model.id) {
          this.clearSelection();
        }
      }
    }
    this.hashModelsById();
    this.setLastUpdated();
  };

  /**
   * Sets the model(s) in the collection. Accepts an array of models/raw objects or a simple model/object.
   */
  @action
  public set = (models: AcceptedModelInputs<MOD>[]): void => {
    // This is a hack to pretend to clear out the existing data and append the new models w/o triggering two observed changes to this.models
    this.original = [];
    this.add(models);
    this.setLastUpdated();
  };

  /**
   * Can be overridden for more header handling, though note that this is entirely ignored unless
   * you pass `rawResponse` to `fetch()` calls.
   */
  @action
  protected processHeaders(headers: Partial<Record<string, string>>): void {
    if (headers['x-total-count'] != null) {
      this.setTotalCount(parseInt(headers['x-total-count']));
    }
  }

  @action
  public processData(
    response: ApiResponse<MOD> | null,
    fetchOptions: FetchOptions<MOD> = {}
  ): Promise<InferModelConstructorArgs<MOD>[] | null> {
    if (response?.body && fetchOptions.rawResponse) {
      const { headers = {} } = response;
      this.processHeaders(headers);
      response = safelyParseJSON(response.body) as ApiResponse<MOD>;
    }
    // do not clear original if we're adding more on top of it (for example loading more data via infinite scroll)
    if (!this.useAsyncAdd || !(fetchOptions?.data?.offset || fetchOptions?.data?.pagination?.offset)) {
      this.original = [];
    }

    this.hasMoreResults = response?.pagination?.has_more_results;

    const data = response ? this.deserialize(response) : [];

    if (this.useAsyncAdd || fetchOptions?.data?.offset || fetchOptions?.data?.pagination?.offset) {
      let models: MOD[] = [];
      return processLargeArrayAsync<InferModelConstructorArgs<MOD>>(data, (row) => {
        models = models.concat(this.build(row));
      }).then(
        action(() => {
          this.original = this.get().concat(models);
          this.filter(undefined, {
            idPath: fetchOptions?.idPath,
            guardForDuplicates: fetchOptions?.pagedFetch
          });
          this.setLastUpdated();
          this.hashModelsById();

          return response;
        })
      );
    }

    this.set(data);

    return Promise.resolve(response);
  }

  /**
   * Creates a model object of the type the Collection instance uses with the provided data
   * optionally selecting it (this is the default behavior). The resultant model is not added
   * to the collection!
   */
  @action
  public forge = (attributes: AcceptedModelInputs<MOD>, options: { select?: boolean } = {}): MOD => {
    const { select = true } = options;
    return (attributes?.id && this.get(attributes.id)) || this.build(attributes, { ...options, select });
  };

  private canReuseFetch(this: this, options: FetchOptions<MOD>, label?: RequestStatus): boolean {
    return !!(
      !options.pagedFetch &&
      !options.query &&
      !options.force &&
      ((this.requestStatus === label && this.fetchPromise) ||
        (this.lastFetched && this.lastFetched > Date.now() - this.minFetchInterval))
    );
  }

  /**
   * Fetches collection data from the backend and overwrites the models, applying filter/sorts/groups.
   */
  @action
  public async fetch(options: FetchOptions<MOD> = {}): Promise<InferModelConstructorArgs<MOD>[] | null> {
    const { preserveSelection } = options;

    // fetching more in case we're doing a paged fetch, to update the UI somehow, indicate to the user we are loading more
    const label = options.pagedFetch ? 'fetchingMore' : 'fetching';

    if (options.pagedFetch) {
      this.useAsyncAdd = true;
    }

    // Never fetch if already fetching or if too recent - unless parameters are diff
    if (this.canReuseFetch(options, label) && isEqual(options.data, this.lastData)) {
      return this.fetchPromise;
    }

    this.lastFetched = Date.now();
    this.requestStatus = label;
    this.hasFetched = true;
    this.fetchCount += 1;
    this.lastData = options.data;
    const count = this.fetchCount;

    if (!preserveSelection) {
      this.clearSelection();
    }

    this.fetchPromise = this.doFetch(options, label)
      .then(
        (response) => {
          // ensure that we only display the last result if we make multiple fetches (i.e. due to options.force)
          if (this.fetchCount === count) {
            return this.processData(response, options);
          }
          // this.fetchPromise will always contain the latest attempt so we can simply chain that
          return this.fetchPromise;
        },
        (error: unknown) => {
          if (this.fetchCount === count) {
            this.error = { label, body: error };
            this.models.clear();
            this.hashModelsById();
            this.fetchPromise = null;
          }
          throw error;
        }
      )
      .finally(() => {
        if (this.fetchCount === count) {
          this.requestStatus = null;
        }
      });

    return this.fetchPromise;
  }

  private getCacheKey(data: FetchOptions<MOD>): string {
    return JSON.stringify(data);
  }

  private getCachedValue(cacheOptions: FetchOptions<MOD>): ApiResponse<MOD> | undefined {
    return this.cachedResults.get(this.getCacheKey(cacheOptions));
  }

  private saveToCache(result: ApiResponse<MOD>, cacheOptions: FetchOptions<MOD>): void {
    this.cachedResults.set(this.getCacheKey(cacheOptions), result);
  }

  /**
   * Just performs the actual data fetching. This method can be overridden for custom fetch mechanisms.
   */
  private async doFetch(options: FetchOptions<MOD> = {}, label?: RequestStatus): Promise<ApiResponse<MOD> | null> {
    if (options.withCache && this.canReuseFetch(options, label)) {
      const cachedValue = this.getCachedValue(options);
      if (cachedValue) {
        return Promise.resolve(cachedValue);
      }
    }
    const { data, fetchUrl, fetchMethod, query, rawResponse } = options;
    const httpMethod = fetchMethod || this.fetchMethod;
    const url = fetchUrl || (this.urlPaths?.fetch ? this.urlPaths.fetch : this.url);
    if (!url) {
      throw new Error('Could not find URL for Collection fetch!');
    }

    return api[httpMethod](url, { data, query, rawResponse }).then((result) => {
      if (options.withCache) {
        this.saveToCache(result, options);
      }
      return result;
    });
  }

  private getServerSortBy = (): string | undefined => {
    const canServerSort =
      this.sortFieldIsServerSortable ||
      (typeof this.groupBy === 'string' && this.serverSortableFields.includes(this.groupBy));
    let sortBy: string | undefined;

    if (canServerSort) {
      // first sorting is done by grouping in terms of api search
      sortBy = (typeof this.groupBy === 'string' && this.groupBy) || '';

      if (this.sortState.field && this.sortFieldIsServerSortable) {
        // second sorting is done by actual sort on a specific field - comma separated
        // format: groupBy,sortBy:order
        sortBy = sortBy
          ? `${sortBy},${this.sortState.field}:${this.sortState.direction}`
          : `${this.sortState.field}:${this.sortState.direction}`;
      }
    }

    return sortBy;
  };

  public loadMoreItems = ({
    startIndex,
    reset = false,
    force = false
  }: {
    startIndex?: number;
    reset?: boolean;
    force?: boolean;
  }): Promise<void> | Promise<InferModelConstructorArgs<MOD>[] | null> => {
    const sortBy = this.getServerSortBy();
    const isUnfiltered = this.size === this.unfilteredSize;
    // If there's a collection filter, use the unfiltered size as our offset
    const offset = isUnfiltered ? startIndex : this.unfilteredSize;
    const hadMoreResults = this.hasMoreResults;

    if (reset) {
      this.reset();
    }

    // this.totalCount represents total possible results for a query.
    // If our offset is greater/equal to that, no need to kick off another fetch.
    if (this.totalCount != null && offset && offset >= this.totalCount && !hadMoreResults) {
      return Promise.resolve(null);
    }

    return this.fetch({
      pagedFetch: !!startIndex,
      data: {
        force,
        offset,
        sortBy: sortBy?.toString()
      }
    });
  };

  /**
   * This is very similar to fetch but allows for multiple fetch requests to go out at same time, but ONLY
   * last one will actually update the collection with results (paging, server-based collection filtering, etc.)
   */
  @action
  public async queuedFetch(this: this, options: FetchOptions<MOD> = {}): Promise<InferModelType<MOD>[] | null> {
    const { data, fetchUrl, fetchMethod, preserveSelection, query } = options;

    const label: RequestStatus = 'fetching';

    this.requestStatus = label;
    this.hasFetched = true;
    this.useAsyncAdd = false;

    if (!preserveSelection) {
      this.clearSelection();
    }

    const key = this.queuedFetchKey;
    if (!key) {
      return Promise.reject(new Error('Collection must provide queuedFetchKey'));
    }
    const qfHttpMethod = fetchMethod || this.fetchMethod;
    const qfUrl = fetchUrl || (this.urlPaths && this.urlPaths.fetch ? this.urlPaths.fetch : this.url);

    if (!qfUrl) {
      throw new Error('Could not fetch Collection with missing qfUrl');
    }

    const { masterPromise, queue } = collatePromises({ key, promise: api[qfHttpMethod](qfUrl, { data, query }) });

    if (queue.length > 1) {
      return masterPromise;
    }

    return masterPromise
      .then(
        (results: ApiResponse<MOD>) => this.processData(results),
        (error: unknown) => {
          this.error = { label, body: error };
          this.models.clear();
          this.hashModelsById();
          throw error;
        }
      )
      .finally(() => {
        this.requestStatus = null;
      });
  }

  protected get selectedIds(): (string | number)[] | undefined {
    const { selectedArray } = this;
    return selectedArray?.map((model) => model.id);
  }

  public async bulkUpdate<K extends keyof InferModelType<MOD>>(
    this: this,
    name: InferModelType<MOD>[K],
    value: InferModelType<MOD>[InferModelType<MOD>[K]]
  ) {
    const url = this.url || this.urlPaths?.fetch;
    const { selectedArray, selectedIds } = this;
    if (!selectedIds?.length || !url) {
      return null;
    }
    const data = { ids: selectedIds, name, value };

    this.setRequestStatus('updating');
    return api
      .post(`${url}/bulkUpdate`, { data })
      .then((results) => {
        if (Array.isArray(results) && results[0]?.id) {
          // received an array of updated models, so update models directly
          results.forEach((result) => {
            const model = this.get(result.id);
            if (model) {
              model.set(model.deserialize(result));
            }
          });
        } else {
          // didn't receive full updated data, so just update the specific field
          selectedArray.forEach((model) => {
            model.set(name, value);
          });
        }
      })
      .finally(() => {
        this.setRequestStatus(null);
      });
  }

  public async bulkDelete(this: this, { removeFromCollection = true } = {}): Promise<any> {
    const { selectedArray } = this;
    const url = this.url || this.urlPaths?.fetch;
    if (!selectedArray?.length || !url) {
      return null;
    }

    const ids = selectedArray?.map((model) => model.id);
    const data = { ids };
    this.setRequestStatus('updating');
    return api.post(`${url}/bulkDelete`, { data }).then((result) => {
      if (removeFromCollection) {
        this.remove(ids);
      }
      this.clearSelection();
      this.setRequestStatus(null);

      return result;
    });
  }

  public serialize(this: this, data?: Partial<InferModelType<MOD>>[]): Partial<InferModelType<MOD>>[] {
    return data || this.map((model) => model.serialize());
  }

  protected get jsonRoot(): string | undefined {
    return undefined;
  }

  public deserialize(this: this, data: InferModelConstructorArgs<MOD>[]): InferModelConstructorArgs<MOD>[] {
    // @ts-expect-error this.jsonRoot isn't really something we can map easily
    return data && this.jsonRoot ? data[this.jsonRoot] : data;
  }

  @action
  private setTotalCount(this: this, totalCount: number): void {
    this.totalCount = totalCount;
  }
}

/**
 * A concrete, non-generic version of the Collection class that can be
 * easily instantiated from JavaScript files.
 *
 * @deprecated for future code it is generally preferable to create your own type-safe Collection
 */
export default class GenericCollection extends Collection<GenericModel> {
  // Provide a default implementation for the model getter
  get model() {
    return GenericModel;
  }
}
